/**
 * Test ID utilities for consistent test automation
 *
 * This module provides utilities for managing data-testid attributes
 * used in E2E testing with Playwright or other testing frameworks.
 */

/**
 * Common interface for components that support test IDs
 */
export interface WithTestId {
  testId?: string;
}

/**
 * Builds a composite test ID by joining a parent ID with a suffix
 *
 * @param parentId - The parent test ID (optional)
 * @param suffix - The suffix to append
 * @returns A composite test ID or undefined if parentId is not provided
 *
 * @example
 * buildTestId('login-form', 'submit-button') // Returns: 'login-form-submit-button'
 * buildTestId(undefined, 'submit-button')    // Returns: undefined
 * buildTestId('login-form', 'email', 'input') // Returns: 'login-form-email-input'
 */
export function buildTestId(
  parentId: string | undefined,
  ...suffixes: Array<string | number>
): string | undefined {
  if (!parentId) return;

  const cleanedSuffixes = suffixes
    .filter(
      (suffix) => suffix !== undefined && suffix !== null && suffix !== ''
    )
    .map((suffix) => String(suffix));

  if (cleanedSuffixes.length === 0) return parentId;

  return [parentId, ...cleanedSuffixes].join('-');
}

/**
 * Creates a test ID getter function bound to a specific parent ID
 * Useful for components that need to create multiple child test IDs
 *
 * @param parentId - The parent test ID (optional)
 * @returns A function that creates child test IDs
 *
 * @example
 * const getTestId = createTestIdGetter('modal');
 * getTestId('header')  // Returns: 'modal-header'
 * getTestId('footer')  // Returns: 'modal-footer'
 *
 * const getTestId = createTestIdGetter(undefined);
 * getTestId('header')  // Returns: undefined
 */
export function createTestIdGetter(parentId: string | undefined) {
  return (...suffixes: Array<string | number>) =>
    buildTestId(parentId, ...suffixes);
}

/**
 * Builds test ID props object for HTML elements
 * Returns an object with data-testid property if testId is provided
 *
 * @param testId - The test ID (optional)
 * @returns Props object with data-testid or empty object
 *
 * @example
 * <div {...getTestIdProps(testId)}>Content</div>
 * // If testId='button', renders: <div data-testid="button">Content</div>
 * // If testId=undefined, renders: <div>Content</div>
 */
export function getTestIdProps(testId: string | undefined): {
  'data-testid'?: string;
} {
  return testId ? { 'data-testid': testId } : {};
}

/**
 * Type guard to check if props include testId
 */
export function hasTestId(props: any): props is WithTestId {
  return props && typeof props === 'object' && 'testId' in props;
}
